import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from 'environment/environment';

import { ScreeningConclusion, ScreeningConclusionPayload } from 'modules/shared/models/screening.models';
import { VerificationConclusion } from 'modules/shared/models/record.models';
import {
  VerificationConclusionsResponse, RecordsPayload, VerificationConclusionsPayload,
  UpdateVerificationConclusionPayload, RecordFaceComparesResponse, RecordOcrsResponse
} from 'store/models/record.models';
import { RiskLevelChoices, ApprovalStatusChoices, ApprovalStatus } from 'modules/shared/models/risk-assessment.models';
import {
  RiskReportPayload, RiskReportResponse, CreateApprovalStatusPayload, ApprovalStatusPayload, ApprovalStatusResponse,
} from 'store/models/risk-assessment.models';

@Injectable()
export class RiskAssessmentService {
  readonly apiBase: string = environment.API_BASE;

  constructor(private httpClient: HttpClient) {}

  getRiskReports(payload: RiskReportPayload): Observable<RiskReportResponse> {
    let url = `${this.apiBase}/api/customers/${payload.customerId}/risk_reports/?`;
    url += payload.limit ? `limit=${payload.limit}` : '';
    url += payload.offset ? `&offset=${payload.offset}` : '';
    url += payload.search ? `&search=${payload.search}` : '';
    url += payload.sort ? `&sort=${payload.sort}` : '';
    return this.httpClient.get<RiskReportResponse>(url);
  }

  getOcrResults(payload: RecordsPayload): Observable<RecordOcrsResponse> {
    let url = `${this.apiBase}/api/records/${payload.id}/ocrs/?`;
    url += payload.limit ? `limit=${payload.limit}` : '';
    url += payload.offset ? `&offset=${payload.offset}` : '';
    url += payload.search ? `&search=${payload.search}` : '';
    url += payload.sort ? `&sort=${payload.sort}` : '';
    return this.httpClient.get<RecordOcrsResponse>(url);
  }

  getFaceResults(payload: RecordsPayload): Observable<RecordFaceComparesResponse> {
    let url = `${this.apiBase}/api/records/${payload.id}/facial_recognitions/?`;
    url += payload.limit ? `limit=${payload.limit}` : '';
    url += payload.offset ? `&offset=${payload.offset}` : '';
    url += payload.search ? `&search=${payload.search}` : '';
    url += payload.sort ? `&sort=${payload.sort}` : '';
    return this.httpClient.get<RecordFaceComparesResponse>(url);
  }

  getVerificationConclusions(
    payload: VerificationConclusionsPayload
  ): Observable<VerificationConclusionsResponse> {
    let url = this.apiBase;
    url += `/api/crps/${payload.crpId}/verification_conclusions/?`;
    url += payload.limit ? `limit=${payload.limit}` : '';
    url += payload.offset ? `&offset=${payload.offset}` : '';
    url += payload.search ? `&search=${payload.search}` : '';
    url += payload.sort ? `&sort=${payload.sort}` : '';
    url += payload.verificationType
        ? `&verification_type=${payload.verificationType}`
        : '';
    return this.httpClient.get<VerificationConclusionsResponse>(url);
  }

  updateVerificationConclusions(
    payload: UpdateVerificationConclusionPayload
  ): Observable<VerificationConclusion> {
    const url = `${this.apiBase}/api/crps/${payload.crpId}/verification_conclusions/`;
    const data = {
      verification_type: payload.verificationType,
      verification_result: payload.verificationResult
    };
    return this.httpClient.post<VerificationConclusion>(url, data);
  }

  getRiskLevelChoices(): Observable<RiskLevelChoices[]> {
    const url = `${this.apiBase}/api/approval_statuses/risk_level_choices/`;
    return this.httpClient.get<RiskLevelChoices[]>(url);
  }

  getApprovalStatusChoices(): Observable<ApprovalStatusChoices[]> {
    const url = `${this.apiBase}/api/approval_statuses/approval_status_choices/`;
    return this.httpClient.get<ApprovalStatusChoices[]>(url);
  }

  createScreeningConclusion(payload: ScreeningConclusionPayload): Observable<ScreeningConclusion> {
    const url = `${this.apiBase}/api/crps/${payload.crp}/screening_conclusions/`;
    const data = {
      crp: payload.crp,
      pep: payload.pep,
      sanction: payload.sanction,
      adverse_news: payload.adverseNews,
      no_hit: payload.noHit,
    };
    return this.httpClient.post<ScreeningConclusion>(url, data);
  }

  getApprovalStatuses(payload: ApprovalStatusPayload): Observable<ApprovalStatusResponse> {
    let url = `${this.apiBase}/api/customers/${payload.customerId}/approval_statuses/?`;
    url += payload.limit ? `limit=${payload.limit}` : '';
    url += payload.offset ? `&offset=${payload.offset}` : '';
    url += payload.search ? `&search=${payload.search}` : '';
    url += payload.sort ? `&sort=${payload.sort}` : '';
    url += payload.cursor ? `&cursor=${payload.cursor}` : '';
    return this.httpClient.get<ApprovalStatusResponse>(url);
  }

  createApprovalStatus(
    payload: CreateApprovalStatusPayload
  ): Observable<ApprovalStatus> {
    const url = `${this.apiBase}/api/risk_reports/${payload.riskReport}/approval_statuses/`;
    const data = {
      approval_status: payload.approvalStatus,
      override_risk: payload.overrideRisk,
      risk_report: payload.riskReport,
      notify_person: payload.notifyPerson
    };
    return this.httpClient.post<ApprovalStatus>(url, data);
  }
}
